import React, { useState, useEffect } from "react";
import { X, Trash2, Plus, Info, Edit } from "lucide-react";
import { AddTraderOffersData } from "../../../../Rest_api/RestApi";
import OfferListTable from "./AddOfferTable";

import { useSelector } from "react-redux";
import TimeInput from "../../../../components/TimeInput/TimeInput";


interface TimeSlot {
  id: string;
  start_slot: string;
  end_slot: string;
  quantum: string;
  rate: string;
  label?: string;
}

interface OfferRow {
  id: string;
  startDate: string;
  endDate: string;
  type: "RTC" | "PEAK" | "CUSTOM";
  timeSlots: TimeSlot[];
}

interface AddOfferDataModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  modalTitle?: string;
  onSubmit: (data: OfferRow[]) => void;
  initialData?: OfferRow[];
  loading: any;
  setLoading: any;
}



const AddOfferDataModal: React.FC<AddOfferDataModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  modalTitle = "Add Offers",
  onSubmit,
  initialData,
  loading,
  setLoading,
}) => {
  const SelectedStartDate = useSelector(
    (state: any) => state.ProcurementOptimizerGridcoDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.ProcurementOptimizerGridcoDate.endDate
  );
  const [offers, setOffers] = useState<OfferRow[]>(
    initialData || [
      {
        id: "1",
        startDate: "",
        endDate: "",
        type: "RTC",
        timeSlots: [
          {
            id: "slot-1",
            start_slot: "00:00",
            end_slot: "24:00",
            quantum: "",
            rate: "",
          },
        ],
      },
    ]
  );



  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);

  const validateOffers = () => {
    const newErrors: Record<string, string> = {};

    offers.forEach((offer) => {
      if (!offer.startDate) {
        newErrors[`${offer.id}-startDate`] = "Required";
      }
      if (!offer.endDate) {
        newErrors[`${offer.id}-endDate`] = "Required";
      }

      offer.timeSlots.forEach((slot) => {
        if (offer.type !== "RTC") {
          if (!slot.start_slot) {
            newErrors[`${offer.id}-${slot.id}-start_slot`] = "Required";
          }
          if (!slot.end_slot) {
            newErrors[`${offer.id}-${slot.id}-end_slot`] = "Required";
          }
        }

        if (!slot.quantum) {
          newErrors[`${offer.id}-${slot.id}-quantum`] = "Required";
        }
        if (!slot.rate) {
          newErrors[`${offer.id}-${slot.id}-rate`] = "Required";
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddOffer = () => {
    const newOffer: OfferRow = {
      id: Date.now().toString(),
      startDate: "",
      endDate: "",
      type: "RTC",
      timeSlots: [
        {
          id: `slot-${Date.now()}`,
          start_slot: "00:00",
          end_slot: "24:00",
          quantum: "",
          rate: "",
        },
      ],
    };
    setOffers([...offers, newOffer]);
  };

  const handleDeleteOffer = (id: string) => {
    if (offers.length > 1) {
      setOffers(offers.filter((offer) => offer.id !== id));

    }
  };

  const handleUpdateOffer = (id: string, field: string, value: any) => {
    setOffers(
      offers.map((offer) => {
        if (offer.id === id) {
          // If updating start date and end date is before it, clear end date
          if (field === "startDate" && offer.endDate && value > offer.endDate) {
            return { ...offer, [field]: value, endDate: "" };
          }
          return { ...offer, [field]: value };
        }
        return offer;
      })
    );
    if (errors[`${id}-${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${id}-${field}`];
      setErrors(newErrors);
    }
  };

  const handleTypeChange = (id: string, newType: "RTC" | "PEAK" | "CUSTOM") => {
    setOffers(
      offers.map((offer) => {
        if (offer.id === id) {
          if (newType === "RTC") {
            return {
              ...offer,
              type: "RTC",
              timeSlots: [
                {
                  id: `slot-${Date.now()}`,
                  start_slot: "00:00",
                  end_slot: "24:00",
                  quantum: "",
                  rate: "",
                },
              ],
            };
          } else if (newType === "PEAK") {
            return {
              ...offer,
              type: "PEAK",
              timeSlots: [
                {
                  id: `slot-${Date.now()}-1`,
                  start_slot: "",
                  end_slot: "",
                  quantum: "",
                  rate: "",
                  label: "Morning Peak",
                },
                {
                  id: `slot-${Date.now()}-2`,
                  start_slot: "",
                  end_slot: "",
                  quantum: "",
                  rate: "",
                  label: "Evening Peak",
                },
              ],
            };
          } else {
            return {
              ...offer,
              type: "CUSTOM",
              timeSlots: [
                {
                  id: `slot-${Date.now()}`,
                  start_slot: "",
                  end_slot: "",
                  quantum: "",
                  rate: "",
                },
              ],
            };
          }
        }
        return offer;
      })
    );
  };

  const handleAddTimeSlot = (offerId: string) => {
    setOffers(
      offers.map((offer) => {
        if (offer.id === offerId) {
          const newSlot: TimeSlot = {
            id: `slot-${Date.now()}`,
            start_slot: "",
            end_slot: "",
            quantum: "",
            rate: "",
          };
          return {
            ...offer,
            timeSlots: [...offer.timeSlots, newSlot],
          };
        }
        return offer;
      })
    );
  };

  const handleDeleteTimeSlot = (offerId: string, slotId: string) => {
    setOffers(
      offers.map((offer) => {
        if (offer.id === offerId && offer.timeSlots.length > 1) {
          return {
            ...offer,
            timeSlots: offer.timeSlots.filter((slot) => slot.id !== slotId),
          };
        }
        return offer;
      })
    );

  };

  const handleUpdateTimeSlot = (
    offerId: string,
    slotId: string,
    field: string,
    value: any
  ) => {
    setOffers(
      offers.map((offer) => {
        if (offer.id === offerId) {
          return {
            ...offer,
            timeSlots: offer.timeSlots.map((slot) =>
              slot.id === slotId ? { ...slot, [field]: value } : slot
            ),
          };
        }
        return offer;
      })
    );
    const errorKey = `${offerId}-${slotId}-${field}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  const handleSubmit = () => {
    setShowErrors(true);
    if (validateOffers()) {
      onSubmit(offers);
      // setIsModalOpen(false);
      setShowErrors(false);
      setLoading(true);
      // Reset to initial state after submit
      setOffers([
        {
          id: "1",
          startDate: "",
          endDate: "",
          type: "RTC",
          timeSlots: [
            {
              id: "slot-1",
              start_slot: "00:00",
              end_slot: "24:00",
              quantum: "",
              rate: "",
            },
          ],
        },
      ]);

      setErrors({});
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setShowErrors(false);
    // Reset to initial state on close
    setOffers([
      {
        id: "1",
        startDate: "",
        endDate: "",
        type: "RTC",
        timeSlots: [
          {
            id: "slot-1",
            start_slot: "00:00",
            end_slot: "24:00",
            quantum: "",
            rate: "",
          },
        ],
      },
    ]);

    setErrors({});
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <h2 className="text-xl font-medium text-[#525062]">{modalTitle}</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X strokeWidth={3} size={24} />
          </button>
        </div>
       
 
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-[#F3F5F8]">
          {showErrors && Object.keys(errors).length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm font-medium">
                Please fill all required fields
              </p>
            </div>
          )}

          <div className="space-y-6">
            {offers.map((offer, offerIndex) => (
              <div key={offer.id} className="bg-white rounded-lg">
                {/* Header with Offer Number and Delete */}
                <div className="flex items-center justify-between w-[100%] px-4 py-3 mb-3 border-b-2 border-[#DFEAF2]">
                  <h3 className="text-sm font-medium text-[#34656D] ">
                    Offer {offerIndex + 1}
                  </h3>
                  <div className="flex gap-2 mr-3">
                    <button
                      onClick={() => handleDeleteOffer(offer.id)}
                      className="p-1 rounded-lg border border-[#E2E2E5] bg-[#F3F3F4] cursor-pointer hover:bg-gray-100 transition-colors text-gray-400"
                      aria-label="Delete offer"
                      disabled={offers.length === 1}
                    >
                      <Trash2 strokeWidth={3} size={18} />
                    </button>
                    <button
                      onClick={handleAddOffer}
                      className="p-1 rounded-lg border border-[#F1935C] bg-[#FFFAF6] cursor-pointer hover:bg-orange-50 transition-colors text-[#FF8C42]"
                      aria-label="Add offers"
                    >
                      <Plus strokeWidth={3} size={18} />
                    </button>
                  </div>
                </div>
                <div className="px-4 py-3">
                  {/* First Row: Type, Start Date, End Date */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                     
 
                    <div>
                      <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                        Type
                      </label>
                      <select
                        value={offer.type}
                        onChange={(e) =>
                          handleTypeChange(
                            offer.id,
                            e.target.value as "RTC" | "PEAK" | "CUSTOM"
                          )
                        }
                        className="w-full px-3 py-2 !h-[45px] border border-[#E2E2E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white cursor-pointer text-[#6F6D7C] font-medium"
                      >
                        <option value="RTC">RTC</option>
                        <option value="PEAK">PEAK</option>
                        <option value="CUSTOM">CUSTOM</option>
                      </select>
                    </div>

                    <div>
                      <label className="flex items-center gap-1 text-sm font-medium mb-2 text-[#6F6D7C]">
                        Start Date
                        {/* <Info size={14} className="text-gray-400" /> */}
                      </label>
                      <input
                        type="date"
                        value={offer.startDate}
                        onChange={(e) =>
                          handleUpdateOffer(
                            offer.id,
                            "startDate",
                            e.target.value
                          )
                        }
                        min={SelectedStartDate}
                        max={SelectedEndDate}
                        className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-[#6F6D7C] font-medium ${
                          showErrors && errors[`${offer.id}-startDate`]
                            ? "border-red-500"
                            : "border-[#E2E2E5]"
                        }`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={offer.endDate}
                        onChange={(e) =>
                          handleUpdateOffer(offer.id, "endDate", e.target.value)
                        }
                        min={
                          offer.startDate ? offer.startDate : SelectedStartDate
                        }
                        max={SelectedEndDate}
                        // disabled={!offer.startDate}
                        className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-[#6F6D7C] font-medium
                          
                        ${
                          showErrors && errors[`${offer.id}-endDate`]
                            ? "border-red-500"
                            : "border-[#E2E2E5]"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div className="space-y-4">
                    {offer.timeSlots.map((slot, slotIndex) => {


                      return (
                        <div key={slot.id}>
                          {/* Label for Peak type */}
                          {offer.type === "PEAK" && slot.label && (
                            <div className="text-sm font-medium mb-2 text-[#FF8C42]">
                              {slot.label}
                            </div>
                          )}

                          <div className="grid grid-cols-4 gap-4 items-start">
                            <div>
                              <label className="flex items-center gap-1 text-sm font-medium mb-2 text-[#6F6D7C]">
                                Start Time
                                {/* <Info size={14} className="text-gray-400" /> */}
                              </label>
                              {offer.type === "RTC" ? (
                                <div className="w-full px-3 py-2.5 border border-[#E2E2E5] rounded-lg bg-gray-100 cursor-not-allowed text-gray-700 flex items-center h-[40px]">
                                  <span className="text-sm text-[#6F6D7C] font-medium">
                                    00:00
                                  </span>
                                </div>
                              ) : (
                                <div  className={`w-full h-[44px] px-2 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 `}>
                                  <TimeInput
                                    onChange={(val) =>
                                      handleUpdateTimeSlot(
                                        offer.id,
                                        slot.id,
                                        "start_slot",
                                        val
                                      )
                                    }
                                    height={32}
                                    type="startTime"
                                    value={slot.start_slot}
                                    heading={""}
                                  />
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                                End Time
                              </label>
                              {offer.type === "RTC" ? (
                                <div className="w-full px-3 py-2.5 border border-[#E2E2E5] rounded-lg bg-gray-100 cursor-not-allowed text-gray-700 flex items-center h-[40px]">
                                  <span className="text-sm text-[#6F6D7C] font-medium">
                                    24:00
                                  </span>
                                </div>
                              ) : (
                                <div  className={`w-full h-[44px] px-2 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 `}>
                                  <TimeInput
                                    onChange={(val) =>
                                      handleUpdateTimeSlot(
                                        offer.id,
                                        slot.id,
                                        "end_slot",
                                        val
                                      )
                                    }
                                    height={32}
                                    type="endTime"
                                    value={slot.end_slot}
                                    heading={""}
                                    minTime={slot.start_slot}
                                  />
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                                Quantum (MW)
                              </label>
                              <input
                                type="text"
                                value={slot.quantum}
                                onChange={(e) =>
                                  handleUpdateTimeSlot(
                                    offer.id,
                                    slot.id,
                                    "quantum",
                                    e.target.value
                                  )
                                }
                                className={`w-full px-2 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-[#6F6D7C] font-medium ${
                                  showErrors &&
                                  errors[`${offer.id}-${slot.id}-quantum`]
                                    ? "border-red-500"
                                    : "border-[#E2E2E5]"
                                }`}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                                Rate (₹/kWh)
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={slot.rate}
                                  onChange={(e) =>
                                    handleUpdateTimeSlot(
                                      offer.id,
                                      slot.id,
                                      "rate",
                                      e.target.value
                                    )
                                  }
                                  className={`w-full px-2 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-[#6F6D7C] font-medium ${
                                    showErrors &&
                                    errors[`${offer.id}-${slot.id}-rate`]
                                      ? "border-red-500"
                                      : "border-[#E2E2E5]"
                                  }`}
                                />
                                {offer.type === "CUSTOM" && (
                                  <>
                                    {slotIndex ===
                                    offer.timeSlots.length - 1 ? (
                                      <button
                                        onClick={() =>
                                          handleAddTimeSlot(offer.id)
                                        }
                                        className="p-1 rounded-lg border border-[#F1935C] bg-[#FFFAF6] cursor-pointer hover:bg-orange-50 transition-colors text-[#FF8C42]"
                                        aria-label="Add time slot"
                                      >
                                        <Plus strokeWidth={3} size={18} />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() =>
                                          handleDeleteTimeSlot(
                                            offer.id,
                                            slot.id
                                          )
                                        }
                                        className="p-1 rounded-lg border border-[#E2E2E5] bg-[#F3F3F4] cursor-pointer hover:bg-gray-100 transition-colors text-gray-400"
                                        aria-label="Delete time slot"
                                      >
                                        <Trash2 strokeWidth={3} size={18} />
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-center gap-3 bg-white">
          <button
            onClick={handleClose}
            className="flex items-center justify-center px-10 py-2.5 border border-[#8C8A96] text-[#8C8A96] text-xl rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center justify-center px-10 py-2.5 bg-[#F1935C] text-white text-xl rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "Loading..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface AddOfferModalProps {
  onOfferAdded?: () => void;
  refreshKey: any;
  setRefreshKey: any;
}

// Main Component
export default function AddOfferModal({
  onOfferAdded,
  refreshKey,
  setRefreshKey,
}: AddOfferModalProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState<any>(false);

  const handleSubmit = async (data: OfferRow[]) => {
    setLoading(true);
    try {
      // Transform data for API - send as array directly without "offers" key
      // Remove only "id" from timeSlots, keep "label"
      const apiData = data.map((offer) => ({
        type: offer.type,
        start_date: offer.startDate,
        end_date: offer.endDate,
        trader_offer: offer.timeSlots.map(({ id, ...slot }) => slot),
      }));
      // Call your API here
      const res = await AddTraderOffersData(apiData);
      if (res) {
        setRefreshKey((prev: number) => prev + 1);
      }

      // Notify parent that offers were added successfully
      if (data.length > 0 && onOfferAdded) {
        onOfferAdded();
      }
    } catch (error) {
      console.error("Error submitting offers:", error);
      alert("Failed to submit offers. Please try again.");
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  return (
    <div className="w-full bg-white">
      <div className="">
        <div className="bg-[#FFFFFF] rounded-2xl border-[2px] border-[#CAD5E2] p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[#6F6D7C] text-lg font-medium">
             Load Trader Offers
            </span>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center px-4 py-2 gap-2 text-xl rounded-xl bg-[#FFFAF6] text-[#F1935C] font-medium border border-[#F1935C] hover:bg-[#FFF5ED] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Offers{" "}
            </button>
          </div>
          <OfferListTable refreshKey={refreshKey} />
        </div>

        <AddOfferDataModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          modalTitle="Add Offers"
          onSubmit={handleSubmit}
          loading={loading}
          setLoading={setLoading}
        />
      </div>
    </div>
  );
}

// Export the modal component for use in other files
export { AddOfferDataModal };
